import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ComplianceState } from "../../types";
import { axiosObj } from "../../../config/apiClients";
import { ComplianceClientType, ComplianceTransaction, LiveTransactionFilters } from "../../../utils/types";
import { formatDuration } from "../../../utils/date";

const initialPagination = {
  totalItems: 0,
  currentPage: 1,
  totalPages: 1,
  hasNextPage: false,
};

const initialState: ComplianceState = {
  isLoading: false,
  transactions: [],
  pagination: initialPagination,
  isLoadingUsers: false,
  users: [],
  usersPagination: initialPagination,
  isLoadingKycDetail: false,
  kycDetail: null,
  isKycExpiredLoading: false,
  kycExpiredClients: [],
};

type ComplianceTxQuery = {
  paramPage: number;
  paramLimit: number;
  search?: string;
  startDate?: string;
  endDate?: string;
  filters?: Partial<LiveTransactionFilters>;
};

export const getComplianceTransactions = createAsyncThunk(
  "/compliance/transactions",
  async (q: ComplianceTxQuery, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      params.set("paramPage", q.paramPage.toString());
      params.set("paramLimit", q.paramLimit.toString());
      if (q.search) params.set("search", q.search);
      if (q.startDate) params.set("startDate", q.startDate);
      if (q.endDate) params.set("endDate", q.endDate);
      Object.entries(q.filters || {}).forEach(([key, value]) => {
        if (value && value !== "All") params.set(key, value);
      });
      const response = await axiosObj.get(
        `compliance/transactions?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error || { message: "Failed to Fetch Compliance Transactions" }
      );
    }
  }
);

/**
 * Real customers only — dedicated endpoint that (unlike /user/getAll) never
 * hides locked or blacklisted accounts.
 */
export const getComplianceUsers = createAsyncThunk(
  "/compliance/users",
  async (
    query: {
      paramPage: number;
      paramLimit: number;
      search?: string;
      clientType: ComplianceClientType;
      category?: string;
      /** A/C only — the calendar year their deposit total is scoped to. */
      year?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();
      params.set("paramPage", query.paramPage.toString());
      params.set("paramLimit", query.paramLimit.toString());
      params.set("clientType", query.clientType);
      if (query.search) params.set("search", query.search);
      if (query.category && query.category !== "All")
        params.set("category", query.category);
      if (query.category === "A" || query.category === "C") {
        if (query.year) params.set("year", query.year.toString());
      }
      const response = await axiosObj.get(
        `compliance/users?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error || { message: "Failed to Fetch Compliance Users" }
      );
    }
  }
);

/** Notification-shaped list — Compliance clients whose KYC expired today. */
export const getComplianceKycExpired = createAsyncThunk(
  "/compliance/kyc/expired",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosObj.get(`compliance/kyc/expired`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error || { message: "Failed to Fetch Expired Compliance KYC" }
      );
    }
  }
);

/** Full KYC submission for one user. */
export const getComplianceKycUserDetail = createAsyncThunk(
  "/compliance/kyc/detail",
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await axiosObj.post("compliance/kyc/detail", { userId });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error || { message: "Failed to Fetch Compliance KYC Detail" }
      );
    }
  }
);

const complianceSlice = createSlice({
  name: "Compliance",
  initialState,
  reducers: {
    clearComplianceKycDetail(state) {
      state.kycDetail = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getComplianceTransactions.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getComplianceTransactions.fulfilled, (state, action) => {
        state.isLoading = false;
        // Same shaping as the admin Live Transactions view.
        state.transactions = (action.payload?.payload.transactions || []).map(
          (item: ComplianceTransaction) => ({
            ...item,
            dealId: item.dealId ? item.dealId : "-",
            closePrice: item.closePrice ? item.closePrice : null,
            moneyBalance: item.moneyBalance ? item.moneyBalance : undefined,
            metalBalance: item.metalBalance ? item.metalBalance : undefined,
            timeDiff:
              item.typeDetail === "Trade"
                ? formatDuration(item.timeDiff)
                : "-",
            totalPnl: item.totalPnl ? item.totalPnl : null,
          })
        );
        state.pagination = {
          totalItems: action.payload.payload.totalItems,
          currentPage: action.payload.payload.page,
          totalPages: action.payload.payload.totalPages,
          hasNextPage: action.payload.payload.hasNextPage,
        };
      })
      // An empty result / 404 lands here — an empty list is the right state.
      .addCase(getComplianceTransactions.rejected, (state) => {
        state.isLoading = false;
        state.transactions = [];
        state.pagination = initialPagination;
      })
      .addCase(getComplianceUsers.pending, (state) => {
        state.isLoadingUsers = true;
      })
      .addCase(getComplianceUsers.fulfilled, (state, action) => {
        state.isLoadingUsers = false;
        state.users = action.payload.payload.users;
        state.usersPagination = {
          totalItems: action.payload.payload.totalItems,
          currentPage: action.payload.payload.page,
          totalPages: action.payload.payload.totalPages,
          hasNextPage: action.payload.payload.hasNextPage,
        };
      })
      .addCase(getComplianceUsers.rejected, (state) => {
        state.isLoadingUsers = false;
        state.users = [];
        state.usersPagination = initialPagination;
      })
      .addCase(getComplianceKycUserDetail.pending, (state) => {
        state.isLoadingKycDetail = true;
      })
      .addCase(getComplianceKycUserDetail.fulfilled, (state, action) => {
        state.isLoadingKycDetail = false;
        state.kycDetail = action.payload.payload;
      })
      .addCase(getComplianceKycUserDetail.rejected, (state) => {
        state.isLoadingKycDetail = false;
        state.kycDetail = null;
      })
      .addCase(getComplianceKycExpired.pending, (state) => {
        state.isKycExpiredLoading = true;
      })
      .addCase(getComplianceKycExpired.fulfilled, (state, action) => {
        state.isKycExpiredLoading = false;
        state.kycExpiredClients = action.payload?.payload?.clients ?? [];
      })
      .addCase(getComplianceKycExpired.rejected, (state) => {
        state.isKycExpiredLoading = false;
        state.kycExpiredClients = [];
      });
  },
});

export const { clearComplianceKycDetail } = complianceSlice.actions;
export default complianceSlice.reducer;
